import { ReactElement, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { redirectTo } from 'ui/customHooks/useHistory'
import ModalDialogForm from 'ui/molecules/ModalDialogForm'
import ModalHeader from 'ui/molecules/ModalHeader'
import { ActiveAccount, Demographic } from 'typings/demographic'
import { RootState } from 'redux/rootReducer'
import { setActiveAccount } from 'redux/demographic/demographicSlice'
import { updateAppInfoState } from 'redux/appInfo/appInfoSlice'
import { updateCardInfo } from 'redux/accountInfo/accountInfoSlice'
import { userAccountInfo } from 'helpers/user'
import routePaths from 'const/routePaths'
import { getSwitchAccountInfo } from 'redux/switchAccount/switchAccountSlice'
import { useShallowEqualSelector } from 'redux/hooks'
import i18n from 'lang/i18n'
import { ListItemProps, SwitchAccountProps } from './typing'
import {
  GoToAccountButton,
  SwitchAccountBody,
  SwitchAccountFooter,
  SwitchAccountHeader
} from './subcomponents'
import SwitchAccountSearch from './SwitchAccountSearch'
import SwitchAccountListItem from './SwitchAccountListItem'

const SwitchAccount = ({
  switchAccountClick,
  isSwitchAccount,
  closeModal
}: SwitchAccountProps): ReactElement => {
  const dispatch = useDispatch()
  const {
    demographyInfo,
    activeAccountInfo,
    accessToken,
    partialAccountId: switchedPartialAccountId,
    accountId: switchedAccountId,
    relationship
  } = useShallowEqualSelector(({ welcomePanel, authTokenInfo }: RootState) => ({
    demographyInfo: welcomePanel.demographyInfo,
    activeAccountInfo: welcomePanel.activeAccountInfo,
    accessToken: authTokenInfo.tokenInfo.access_token,
    partialAccountId: welcomePanel.activeAccountInfo.accountNumber,
    accountId: welcomePanel.activeAccountInfo.accountID,
    relationship: welcomePanel.activeAccountInfo.relationship
  }))

  const [searchValue, setSearchVal] = useState<string>('')
  const [filteredDemographic, setFilteredDemographic] = useState<Demographic[]>(
    []
  )
  const [activeAccountInformation, setActiveAccountInformation] =
    useState<ActiveAccount>({} as ActiveAccount)
  const [isAccountEnabled, setAccountEnabled] = useState<boolean>(true)

  const setFilteredDemographicInfo = (): void => {
    let modifiedDemographicInfo = [...demographyInfo]
    modifiedDemographicInfo = modifiedDemographicInfo.filter(
      account =>
        account.relationship !== 'PARENT' &&
        account.relationship !== 'Standalone'
    )
    const parentAccount = demographyInfo.find(
      (account: Demographic) =>
        account.relationship === 'PARENT' ||
        account.relationship === 'Standalone'
    )
    if (parentAccount) {
      modifiedDemographicInfo.unshift(parentAccount)
    }
    setFilteredDemographic(modifiedDemographicInfo)
  }

  useEffect(setFilteredDemographicInfo, [demographyInfo])

  const updateActiveAccount = (activeNum = ''): void => {
    const activeAccount = demographyInfo.find((account: Demographic) => {
      return account.accountUniqueIdentifier === activeNum
    }) as ActiveAccount

    const updatedAccount = {
      ...activeAccount,
      accountUniqueIdentifier: activeAccount.accountUniqueIdentifier,
      accountNumber: activeAccount.accountNumber
    }

    setActiveAccountInformation(updatedAccount)
    setAccountEnabled(false)
  }

  const filterSearchResults = (searchText: string): void => {
    const demographicResults = [...demographyInfo]
    const filterDemographic = demographicResults.filter(accnt => {
      return (
        accnt.companyName.toLowerCase().indexOf(searchText.toLowerCase()) > -1
      )
    })
    const parentAccount = filterDemographic.find(
      account =>
        account.relationship === 'PARENT' ||
        account.relationship === 'Standalone'
    )
    if (parentAccount) {
      filterDemographic.unshift(parentAccount)
    }
    setFilteredDemographic(filterDemographic)
  }

  const setSwitchAccountSearch = (searchText = ''): void => {
    setSearchVal(searchText)
    filterSearchResults(searchText)
  }

  const switchAccount = (): void => {
    const { relationship: activeAccountRelationship, accountUniqueIdentifier } =
      activeAccountInformation

    const { accountID } = activeAccountInfo
    const isParent = activeAccountRelationship === 'PARENT'
    const switchAccountRequest = {
      accessToken,
      requestBody: {
        // This is the partialAccountId of current active account
        partialAccountId: switchedPartialAccountId,
        accountUniqueIdentifier,
        relationship
      }
    }
    dispatch(getSwitchAccountInfo(switchAccountRequest))

    let accountDetail = {
      'cipher.accountId': switchedAccountId,
      'cipher.partialAccountId': switchedPartialAccountId,
      switchAccountType: 'SELF'
    }
    if (!isParent) {
      accountDetail = {
        ...accountDetail,
        'cipher.partialAccountId': accountID,
        switchAccountType: 'CHILD'
      }
    }

    dispatch(setActiveAccount(activeAccountInformation))

    userAccountInfo.accountDetail = accountDetail

    dispatch(
      updateCardInfo({
        isDataLoading: true
      })
    )
    dispatch(
      updateAppInfoState({
        isParentAccount: isParent
      })
    )
    switchAccountClick()
    redirectTo(routePaths.summary)
  }

  const handleAccountChange = (): void => {
    if (isSwitchAccount) {
      setAccountEnabled(true)
      setActiveAccountInformation({} as ActiveAccount)
    }
  }

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(handleAccountChange, [isSwitchAccount])

  const activeAccountValue =
    activeAccountInformation.accountUniqueIdentifier ||
    activeAccountInfo.accountUniqueIdentifier

  const { relationship: secondaryRelationship } = activeAccountInformation

  return (
    <ModalDialogForm
      modalId="switchAccount"
      modalTitle="Switch Accounts"
      isOpen={isSwitchAccount}
      onCloseModal={closeModal}
      headerOverride={
        <SwitchAccountHeader>
          <ModalHeader headerText="Switch Accounts" closeButton={closeModal} />
          <SwitchAccountSearch
            searchVal={searchValue}
            searchAccount={setSwitchAccountSearch}
          />
        </SwitchAccountHeader>
      }
      footerOverride={
        <SwitchAccountFooter>
          <GoToAccountButton
            buttonType="primary"
            onClick={switchAccount}
            disabled={isAccountEnabled}
          >
            {i18n({ button: 'Go to Account' })}
          </GoToAccountButton>
        </SwitchAccountFooter>
      }
    >
      <SwitchAccountBody role="list">
        {filteredDemographic.map((listItem: ListItemProps) => {
          return (
            <SwitchAccountListItem
              key={listItem.accountUniqueIdentifier}
              isPrimary={listItem.relationship === 'PARENT'}
              accountName={listItem.companyName}
              accountNumber={listItem.accountUniqueIdentifier}
              setActiveAccount={updateActiveAccount}
              activeAccount={activeAccountValue}
              maskedAccountNumber={listItem.lastFourDigitAccountNumber}
              relationship={relationship}
              secondaryRelationship={secondaryRelationship}
            />
          )
        })}
      </SwitchAccountBody>
    </ModalDialogForm>
  )
}

export default SwitchAccount
